import React, { useState, useEffect } from 'react';
import { companyService } from './service/company.service';
import { userCompanyService } from './service/userCompany.service';
import { Company } from '../../../types';
import { useUserData } from '../../../hooks/useUserData';

const CompaniesPage: React.FC = () => {
  const { isAuthenticated, isLoading: isAuthLoading } = useUserData();
  const [companies, setCompanies] = useState<Company[]>([]);
  // Usar um Set para IDs de empresas monitoradas para performance O(1) em buscas
  const [watchingCompanyIds, setWatchingCompanyIds] = useState<Set<string>>(new Set());
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [togglingIds, setTogglingIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyWatching, setShowOnlyWatching] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadCompaniesData();
    }
  }, [isAuthenticated]);

  const loadCompaniesData = async () => {
    try {
      setIsDataLoading(true);
      // Carregar dados da API em paralelo para melhor performance
      const [allCompanies, userSubs] = await Promise.all([
        companyService.getAllCompanies(),
        userCompanyService.getUserCompanies(),
      ]);
      
      const watchingCompanyNames = new Set(userSubs.map((sub: any) => sub.company));
      const userCompanyIds = new Set(
        allCompanies
          .filter(company => watchingCompanyNames.has(company.nome))
          .map(company => company.id)
      );

      setCompanies(allCompanies);
      setWatchingCompanyIds(userCompanyIds);
    } catch (error) {
      console.error('Erro ao carregar dados de empresas:', error);
    } finally {
      setIsDataLoading(false);
    }
  };

  const toggleCompanyWatching = async (companyId: string) => {
    setTogglingIds(prev => [...prev, companyId]);
    const isCurrentlyWatching = watchingCompanyIds.has(companyId);

    try {
      let success = false;
      if (isCurrentlyWatching) {
        success = await userCompanyService.removeFromWatchlist(companyId);
      } else {
        success = await userCompanyService.addToWatchlist(companyId);
      }
      
      // Atualização otimista da UI para uma resposta mais rápida
      if (success) {
        setWatchingCompanyIds(prevIds => {
          const newIds = new Set(prevIds);
          if (isCurrentlyWatching) {
            newIds.delete(companyId);
          } else {
            newIds.add(companyId);
          }
          return newIds;
        });
      }
    } catch (error) {
      console.error('Erro ao atualizar lista de empresas:', error);
    } finally {
      setTogglingIds(prev => prev.filter(id => id !== companyId));
    }
  };

  // Lógica de Filtragem simplificada
  const filteredCompanies = companies.filter(company => {
    if (!company) {
      return false;
    }
    const isWatching = watchingCompanyIds.has(company.id);

    const matchesSearch = 
      (company.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (company.ticker || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = showOnlyWatching ? isWatching : true;

    return matchesSearch && matchesFilter;
  });
  
  if (isAuthLoading || (isAuthenticated && isDataLoading)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen-1/2 gap-4">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-700 rounded-full animate-spin"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Minhas empresas</h1>
        <p className="text-lg text-gray-600 mb-6">
          Gerencie as empresas que você deseja monitorar.
        </p>

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Buscar por nome ou símbolo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-sm"
            />
            <svg 
              className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          
          <button
            onClick={() => setShowOnlyWatching(!showOnlyWatching)}
            className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 border flex items-center gap-2 whitespace-nowrap
              ${showOnlyWatching 
                ? 'bg-green-50 border-green-200 text-green-700 shadow-inner' 
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 shadow-sm'}`}
          >
            <svg 
              className={`w-5 h-5 ${showOnlyWatching ? 'text-green-600' : 'text-gray-400'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {showOnlyWatching ? 'Apenas Monitoradas' : 'Todas as Empresas'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCompanies.map(company => {
          const isWatching = watchingCompanyIds.has(company.id);
          const isToggling = togglingIds.includes(company.id);
          
          return (
            <div
              key={company.id}
              className={`bg-white rounded-xl p-6 shadow-sm border transition-all duration-300 flex justify-between items-center
                ${isWatching 
                  ? 'border-green-500 bg-green-50/50 shadow-inner' 
                  : 'border-gray-200 hover:-translate-y-1 hover:shadow-md'}`}
            >
              <div>
                <h3 className={`text-lg font-semibold text-gray-900 mb-1 transition-all
                  ${isWatching ? 'underline decoration-green-500 decoration-2 underline-offset-4' : ''}`}>
                  {company.nome}
                </h3>
                <span className="text-sm text-gray-500 font-medium tracking-wider">{company.ticker}</span>
              </div>
              <button
                disabled={isToggling}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 border cursor-pointer min-w-[100px] flex justify-center items-center
                  ${isToggling
                    ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                    : isWatching 
                      ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600' 
                      : 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700 shadow-sm'}`}
                onClick={() => toggleCompanyWatching(company.id)}
              >
                {isToggling ? (
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                ) : (
                  isWatching ? 'Remover' : 'Adicionar'
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CompaniesPage;

